import { createReducer } from '@common/redux-utils';
import {
  EscrowOfficeActionType,
  EscrowOfficeFailureAction,
  GetEscrowOfficeFailureAction,
  GetEscrowOfficeSuccessAction,
  GetEscrowOfficeStartAction,
  GetEscrowOfficeDetailStartAction,
  GetEscrowOfficeDetailSuccessAction,
  GetEscrowOfficeDetailFailureAction,
  GetEscrowOfficersByOfficeStartAction,
  GetEscrowOfficersByOfficeSuccessAction,
  GetEscrowOfficersByOfficeFailureAction,
  SaveEscrowOfficeStartAction,
  SaveEscrowOfficeSuccessAction,
  EscrowOfficeSearchInitAction,
  EscrowOfficeSearchFailureAction,
  EscrowOfficeSearchSuccessAction
} from './escrow-office.actions';
import { EscrowOfficeViewModel, OfficeSearchViewModel } from '@models/EscrowOffice';
import { OfficerSearchViewModel } from '@models/EscrowOfficer';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface EscrowOfficeState {
  readonly saving?: boolean;
  readonly message?: string;
  readonly officeId?: number;
  readonly escrowOfficeDetailVM: EscrowOfficeViewModel | null;
  readonly query?: string | null;
  readonly escrowOfficeLoading?: boolean;
  readonly escrowOfficePageCount?: number;
  readonly escrowOfficePageNumber?: number;
  readonly escrowOfficePageSize?: number;
  readonly escrowOfficeOrderBy?: string;
  readonly escrowOffices?: OfficeSearchViewModel[];
  readonly escrowOfficers?: OfficerSearchViewModel[];
}

// This is the initial state of the page
const initialState: EscrowOfficeState = {
  message: 'Initial state',
  officeId: undefined,
  escrowOfficeDetailVM: null,
  query: '',
  escrowOfficeLoading: false,
  escrowOfficePageCount: 0,
  escrowOfficePageNumber: 1,
  escrowOfficeOrderBy: 'id',
  escrowOffices: [],
  escrowOfficePageSize: 10,
  escrowOfficers: []
};

/********** Save **********/
function handleSaveEscrowOfficeStart(state: EscrowOfficeState, action: SaveEscrowOfficeStartAction): EscrowOfficeState {
  return {
    ...state,
    saving: true
  };
}

function handleSaveEscrowOfficeSuccess(
  state: EscrowOfficeState,
  action: SaveEscrowOfficeSuccessAction
): EscrowOfficeState {
  if (!action.currentId) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'SUCCESS - escrow office saved - ' + action.currentId,
    officeId: action.currentId
  };
}

function handleSaveEscrowOfficeFailure(state: EscrowOfficeState, action: EscrowOfficeFailureAction): EscrowOfficeState {
  if (!action.errorMessage) {
    return {
      ...state,
      saving: false
    };
  }
  return {
    ...state,
    saving: false,
    message: 'ERROR - save escrow office failed - ' + action.errorMessage
  };
}

/********** Get **********/
function handleGetEscrowOfficeStart(state: EscrowOfficeState, action: GetEscrowOfficeStartAction): EscrowOfficeState {
  return state;
}

function handleGetEscrowOfficeSuccess(
  state: EscrowOfficeState,
  action: GetEscrowOfficeSuccessAction
): EscrowOfficeState {
  if (!action.currentId) {
    return state;
  }
  return {
    ...state,
    message: 'SUCCESS - get escrow office done - ' + action.currentId,
    officeId: action.currentId
  };
}

function handleGetEscrowOfficeFailure(
  state: EscrowOfficeState,
  action: GetEscrowOfficeFailureAction
): EscrowOfficeState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get escrow office failed - ' + action.errorMessage
  };
}

function handleGetEscrowOfficeDetailStart(
  state: EscrowOfficeState,
  action: GetEscrowOfficeDetailStartAction
): EscrowOfficeState {
  return {
    ...state,
    message: 'FETCHING ESCROW OFFICE'
  };
}

function handleGetEscrowOfficeDetailSuccess(
  state: EscrowOfficeState,
  action: GetEscrowOfficeDetailSuccessAction
): EscrowOfficeState {
  if (!action.escrowOfficeDetailVM) {
    return state;
  }

  return {
    ...state,
    message: 'SUCCESS - get escrow office done - ' + action.currentId,
    officeId: action.escrowOfficeDetailVM.id,
    escrowOfficeDetailVM: action.escrowOfficeDetailVM
  };
}

function handleGetEscrowOfficeDetailFailure(
  state: EscrowOfficeState,
  action: GetEscrowOfficeDetailFailureAction
): EscrowOfficeState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get escrow office failed - ' + action.errorMessage
  };
}

function handleGetEscrowOfficersByOfficeStart(
  state: EscrowOfficeState,
  action: GetEscrowOfficersByOfficeStartAction
): EscrowOfficeState {
  return {
    ...state,
    message: 'FETCHING ESCROW OFFICERS'
  };
}

function handleGetEscrowOfficersByOfficeSuccess(
  state: EscrowOfficeState,
  action: GetEscrowOfficersByOfficeSuccessAction
): EscrowOfficeState {
  if (!action.escrowOfficers) {
    return state;
  }

  return {
    ...state,
    message: 'SUCCESS - get escrow officers done - ',
    escrowOfficers: action.escrowOfficers
  };
}

function handleGetEscrowOfficersByOfficeFailure(
  state: EscrowOfficeState,
  action: GetEscrowOfficersByOfficeFailureAction
): EscrowOfficeState {
  if (!action.errorMessage) {
    return state;
  }
  return {
    ...state,
    message: 'ERROR - get escrow officers failed - ' + action.errorMessage
  };
}

/********** Search **********/
function handleEscrowOfficeSearchInit(
  state: EscrowOfficeState,
  action: EscrowOfficeSearchInitAction
): EscrowOfficeState {
  return {
    ...state,
    message: 'Search Init',
    query: action.query,
    escrowOfficeLoading: true
  };
}
function handleEscrowOfficeSearchFailure(
  state: EscrowOfficeState,
  action: EscrowOfficeSearchFailureAction
): EscrowOfficeState {
  return {
    ...state,
    message: action.errorMessage,
    escrowOfficeLoading: false
  };
}
function handleEscrowOfficeSearchSuccess(
  state: EscrowOfficeState,
  action: EscrowOfficeSearchSuccessAction
): EscrowOfficeState {
  return {
    ...state,
    message: 'Search Success!',
    escrowOfficeLoading: false,
    escrowOfficePageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    escrowOfficePageNumber: action.pageNumber,
    escrowOfficePageSize: action.pageSize,
    escrowOfficeOrderBy: action.orderBy,
    escrowOffices: action.result.results
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const EscrowOfficeReducer = createReducer(initialState, {
  /********** Save **********/
  [EscrowOfficeActionType.SaveEscrowOfficeStart]: handleSaveEscrowOfficeStart,
  [EscrowOfficeActionType.SaveEscrowOfficeSuccess]: handleSaveEscrowOfficeSuccess,
  [EscrowOfficeActionType.SaveEscrowOfficeFailure]: handleSaveEscrowOfficeFailure,

  /********** Get **********/
  [EscrowOfficeActionType.GetEscrowOfficeStart]: handleGetEscrowOfficeStart,
  [EscrowOfficeActionType.GetEscrowOfficeSuccess]: handleGetEscrowOfficeSuccess,
  [EscrowOfficeActionType.GetEscrowOfficeFailure]: handleGetEscrowOfficeFailure,
  [EscrowOfficeActionType.GetEscrowOfficeDetailStart]: handleGetEscrowOfficeDetailStart,
  [EscrowOfficeActionType.GetEscrowOfficeDetailSuccess]: handleGetEscrowOfficeDetailSuccess,
  [EscrowOfficeActionType.GetEscrowOfficeDetailFailure]: handleGetEscrowOfficeDetailFailure,
  [EscrowOfficeActionType.GetEscrowOfficersByOfficeStart]: handleGetEscrowOfficersByOfficeStart,
  [EscrowOfficeActionType.GetEscrowOfficersByOfficeSuccess]: handleGetEscrowOfficersByOfficeSuccess,
  [EscrowOfficeActionType.GetEscrowOfficersByOfficeFailure]: handleGetEscrowOfficersByOfficeFailure,

  /********** Search **********/
  [EscrowOfficeActionType.EscrowOfficeSearchInit]: handleEscrowOfficeSearchInit,
  [EscrowOfficeActionType.EscrowOfficeSearchFailure]: handleEscrowOfficeSearchFailure,
  [EscrowOfficeActionType.EscrowOfficeSearchSuccess]: handleEscrowOfficeSearchSuccess
});



// WEBPACK FOOTER //
// ./src/escrow-office/escrow-office.reducer.ts